// Code-aware error copy for the leader approve / decline actions on the join-
// requests admin surface. Those mutations funnel non-2xx through `throwApiError`
// → a `DomainError` carrying the server's `{ code, message?, details? }`.
//
// Before #515 both handlers set the InlineStatus to the thrown error's raw
// `message` — which for a `DomainError` is the machine CODE (`super(error.code)`).
// A leader approving a banned applicant's ghost request therefore saw a literal
// "identity/deactivated", which reads as "nothing happened / unexplained error".
// This maps the codes the approve/decline endpoints actually emit to
// decision-specific copy under `admin.requests.*`; any other code resolves
// through the caller's `useErrorMessage` resolver (the localized `Errors`
// catalog), then the caller's generic key — never surfacing a raw code.
//
// Local to the group-admin feature on purpose (mirrors messages/
// message-error-copy.ts): the decision-specific wording is a product concern
// that lives next to the screen that shows it.

import { type useTranslations } from '@/i18n/translations';

import { parseApiErrorCode } from '../../../lib/api-error';
import type { DescribeError } from '../../../lib/error-message';

type GroupTranslate = ReturnType<typeof useTranslations<'Group'>>;

// The subject-standing gate (`identity/banned` / `identity/deactivated`) is the
// #515 case; `NOT_FOUND` covers a request already decided or withdrawn (incl.
// the ban cascade having revoked it out from under the leader);
// `groups/group_archived` covers approving into an archived group.
const DECISION_CODE_KEY = {
  'identity/banned': 'admin.requests.errorBanned',
  'identity/deactivated': 'admin.requests.errorDeactivated',
  NOT_FOUND: 'admin.requests.errorGone',
  'groups/group_archived': 'admin.requests.errorGroupArchived',
} as const;

export function decisionErrorMessage(
  err: unknown,
  describe: DescribeError,
  t: GroupTranslate,
  fallbackKey: Parameters<GroupTranslate>[0],
): string {
  const code = parseApiErrorCode(err);
  if (code !== null) {
    const key = DECISION_CODE_KEY[code as keyof typeof DECISION_CODE_KEY];
    if (key !== undefined) return t(key);
  }
  return describe(err, t(fallbackKey));
}
